import React, { Fragment, Suspense, useCallback, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { SmartCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import { AISources, useAnalytics } from '../../common';
import { SainAction, useSAINUsageCriteriaMet } from '../../common/controller/analytics';
import { AIAnswerBottomBookend } from '../../common/ui/ai-answer-bookends/ai-answer-bottom-bookend';
import { AIAnswerTopBookend } from '../../common/ui/ai-answer-bookends/ai-answer-top-bookend';
import { extractDate } from '../../common/utils/extract-date';
import { extractLocation } from '../../common/utils/extract-location';
import { QueryIntent } from '../../constants';
import { PersonKnowledgeCard, TeamKnowledgeCard } from '../../knowledge-cards';
import { DateCard } from '../../v2/knowledge-cards/feature-cards/DateCard';
import { LocationCard } from '../../v2/knowledge-cards/feature-cards/LocationCard';
import { AIAnswerBody } from '../ai-answer-body';
import { useAIAnswerContext } from '../ai-answer-context';
import { SearchPageSmartAnswerFooter } from '../ai-answer-footer';
import { AIAnswerContentWarningAndBrandSection } from '../ai-answer-footer/ai-answer-footer';
import { AIAnswerLoadingStream } from '../ai-answer-loading/ai-answer-loading-stream';
import GeneratingAnimation from '../ai-answer-loading/generating-animation';
import {
	aiAnswerCollapseClicked,
	aiAnswerExpandClicked,
	aiAnswerExpandShown,
	onAIAnswerLoadingDialogShown,
	onAIAnswerSourceClick,
} from '../analytics';
import { NLPSearchResultFormat } from '../types';

import { Collapse } from './collapse';
import { messages } from './messages';
import { AIAnswerSearchBorder, GeneratingContainer, GeneratingText } from './styled';
import { useSmartAnswerCardFeedbackOptions } from './use-smart-answer-card-feedback-options';

const MAX_SOURCES = 4;

const spacerStyles = xcss({
	height: '14px',
	paddingTop: 'space.250',
});

const errorContainerStyles = xcss({
	paddingTop: 'space.100',
});

const sourcesContainerStyles = xcss({
	paddingTop: '0',
	paddingRight: 'space.300',
	paddingBottom: 'space.150',
	paddingLeft: 'space.300',
});

type SmartAnswerLoadingDialogProps = {
	collapsed: boolean;
	toggleCollapsedState?: () => void;
	callbacks?: {
		// called when card is rendered in the expanded state due to the answer being short
		onExpandedByDefault?: () => void;
		onExpandClicked?: () => void;
		onCollapseClicked?: () => void;
	};
};

const SmartAnswerLoadingDialog = ({
	collapsed,
	toggleCollapsedState,
	callbacks,
}: SmartAnswerLoadingDialogProps) => {
	const { commonAttributes, partialResponse, showBookends, queryReasoning, smartAnswersRoute } =
		useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const { formatMessage } = useIntl();

	useEffect(() => {
		fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { serveExperiment: serveSAINReasoningExperiment } = getFrontendExperiment({
		abTestId: 'kd_sain_reasoning_exp',
	});

	// no need for the Collapse component when showing the ... generating animation
	if (
		!partialResponse ||
		partialResponse?.format !== NLPSearchResultFormat.MARKDOWN ||
		smartAnswersRoute === QueryIntent.DATE ||
		smartAnswersRoute === QueryIntent.LOCATION
	) {
		return (
			<>
				{showBookends ? <AIAnswerTopBookend showFeedbackButtons={false} /> : null}
				<AIAnswerSearchBorder isLoading isStreaming={false}>
					<GeneratingContainer>
						<GeneratingAnimation alt={formatMessage(messages.ai_answer_generating_icon_alt_text)} />
						<GeneratingText>
							{serveSAINReasoningExperiment && queryReasoning ? (
								queryReasoning
							) : (
								<FormattedMessage {...messages.ai_answer_generating_rovo} />
							)}
						</GeneratingText>
					</GeneratingContainer>
					{!showBookends ? <AIAnswerContentWarningAndBrandSection /> : null}
				</AIAnswerSearchBorder>
				{showBookends ? <AIAnswerBottomBookend /> : null}
			</>
		);
	}
	return (
		<>
			{showBookends ? <AIAnswerTopBookend showFeedbackButtons={false} /> : null}
			<AIAnswerSearchBorder isLoading isStreaming>
				<Suspense fallback={<Fragment />}>
					<SmartCardProvider>
						<Collapse
							collapsed={collapsed}
							isLoading
							isStreaming
							expandWhenClickedAnywhere
							toggleCollapsedState={toggleCollapsedState}
							callbacks={callbacks}
						>
							<AIAnswerLoadingStream markdown={partialResponse.partialNlpResult} />
						</Collapse>
						{!showBookends ? <AIAnswerContentWarningAndBrandSection /> : null}
					</SmartCardProvider>
				</Suspense>
			</AIAnswerSearchBorder>
			{showBookends ? <AIAnswerBottomBookend /> : null}
		</>
	);
};

// TODO: Remove KnowledgeCard block when cleaning kd_smart_answers_render_by_experience fg
const KnowledgeCard = () => {
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { smartAnswersRoute, commonAttributes } = useAIAnswerContext();
	const { trackAIResultView } = useAIEventsInstrumentation();
	useEffect(() => {
		fg('kd_enable_new_usage_tracking')
			? fireSAINUsageEvent({ commonAttributes })
			: trackAIResultView();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	switch (smartAnswersRoute) {
		case QueryIntent.TEAM:
			return <TeamKnowledgeCard />;
		case QueryIntent.PERSON:
			return <PersonKnowledgeCard />;
		default:
			return null;
	}
};

export const SmartAnswerCard = () => {
	const {
		isAnswered,
		hasError,
		commonAttributes,
		callbacks,
		smartAnswersRoute,
		loading,
		showBookends,
	} = useAIAnswerContext();
	const { answerFormat, answerString, query } = commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { trackAIResultAction } = useAIEventsInstrumentation();
	const [collapsed, setCollapsed] = useState(false);
	const feedbackOptions = useSmartAnswerCardFeedbackOptions();

	// reset on each new query
	useEffect(() => {
		setCollapsed(true);
	}, [query]);

	const toggleCollapsedState = () => {
		setCollapsed(!collapsed);
	};

	const analyticsCallbacks = {
		onExpandedByDefault: () => fireAnalyticsEvent(aiAnswerExpandShown(commonAttributes)),
		onExpandClicked: () => {
			fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes));
			fireSAINUsageEvent({ metCriteria: SainAction.EXPAND_CLICKED, commonAttributes });
		},
		onCollapseClicked: () => fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes)),
	};

	const sourceClickCallback = useCallback(
		(index: number, hostname?: string) => {
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: index,
					hostname,
					...commonAttributes,
				}),
			);
			fg('kd_enable_new_usage_tracking')
				? fireSAINUsageEvent({ metCriteria: SainAction.SOURCE_CLICKED, commonAttributes })
				: trackAIResultAction(SainAction.SOURCE_CLICKED);
			callbacks?.onAIAnswerSourceClick?.();
		},
		[fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent, callbacks, trackAIResultAction],
	);

	if (loading) {
		return (
			<SmartAnswerLoadingDialog
				collapsed={collapsed}
				toggleCollapsedState={toggleCollapsedState}
				callbacks={analyticsCallbacks}
			/>
		);
	}

	// TODO: Remove block when cleaning kd_smart_answers_render_by_experience fg
	if (
		isAnswered &&
		!hasError &&
		(smartAnswersRoute === QueryIntent.PERSON || smartAnswersRoute === QueryIntent.TEAM)
	) {
		return (
			<>
				{showBookends ? <AIAnswerTopBookend showFeedbackButtons={false} /> : null}
				<KnowledgeCard />
				{showBookends ? <AIAnswerBottomBookend /> : null}
			</>
		);
	}

	// TODO: Remove block when cleaning kd_smart_answers_render_by_experience fg
	// if there is no team found (either the team or its data)
	// don't render a smart answer card or an error state
	if (!!hasError && smartAnswersRoute === QueryIntent.TEAM) {
		return <></>;
	}

	let answerContent = (
		<Collapse
			collapsed={collapsed}
			toggleCollapsedState={toggleCollapsedState}
			expandWhenClickedAnywhere
			isLoading={false}
			callbacks={analyticsCallbacks}
		>
			<AIAnswerBody />
		</Collapse>
	);

	if (hasError) {
		answerContent = (
			<Box xcss={errorContainerStyles}>
				<AIAnswerBody />
			</Box>
		);
	} else if (answerFormat === NLPSearchResultFormat.JSON) {
		answerContent = (
			<>
				<Box xcss={spacerStyles}></Box>
				<AIAnswerBody />
			</>
		);
	}

	const dateInAnswer = extractDate(answerString);
	if (isAnswered && smartAnswersRoute === QueryIntent.DATE && dateInAnswer) {
		return (
			<>
				{showBookends ? (
					<AIAnswerTopBookend showFeedbackButtons={true} feedbackOptions={feedbackOptions} />
				) : null}
				<AIAnswerSearchBorder isLoading={false} isStreaming={false}>
					<DateCard date={dateInAnswer} />
				</AIAnswerSearchBorder>
				{showBookends ? <AIAnswerBottomBookend /> : null}
			</>
		);
	}

	const locationInAnswer = extractLocation(answerString);
	if (isAnswered && smartAnswersRoute === QueryIntent.LOCATION && locationInAnswer) {
		return (
			<>
				{showBookends ? (
					<AIAnswerTopBookend showFeedbackButtons={true} feedbackOptions={feedbackOptions} />
				) : null}
				<AIAnswerSearchBorder isLoading={false} isStreaming={false}>
					<LocationCard location={locationInAnswer} />
				</AIAnswerSearchBorder>
				{showBookends ? <AIAnswerBottomBookend /> : null}
			</>
		);
	}

	return (
		<>
			{showBookends ? (
				<AIAnswerTopBookend showFeedbackButtons={true} feedbackOptions={feedbackOptions} />
			) : null}
			<AIAnswerSearchBorder isLoading={false} isStreaming={false}>
				{answerContent}
				{!hasError && (
					<Box xcss={sourcesContainerStyles} testId="ai-answer-sources">
						<AISources
							sources={commonAttributes.sources}
							searchSessionId={commonAttributes.searchSessionId}
							onClickHandler={sourceClickCallback}
							allowFreeflowLayout={true}
							limit={MAX_SOURCES}
						/>
					</Box>
				)}
				<SearchPageSmartAnswerFooter />
			</AIAnswerSearchBorder>
			{showBookends ? <AIAnswerBottomBookend /> : null}
		</>
	);
};
